import { DestroyRef, inject, Injectable } from '@angular/core';
import { GsmVirusWarningDialogComponent } from '@app/shared/components/dialog/gsm-virus-warning-dialog/gsm-virus-warning-dialog.component';
import { CustomFileModel } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/dynamic-form-components/custom-file-upload/custom-file-upload.model';
import { EVENT_TYPE_PRINT_PDF_INFOCLIENT } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/dynamic-form-components/nachricht-referent/nachricht-referent.model';
import { GsmErrorService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm-error.service';
import { GsmService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm.service';
import { PeziService } from '@app/shared/services/pezi.service';
import { LowLevelApi } from '@app/shared/services/state/low-level-api.service';
import { DialogService } from '@app/shared/services/ui/dialog/dialog.service';
import { DialogStatus, Mode } from '@app/shared/services/ui/dialog/dialog.types';
import { ReactiveDialogService } from '@app/shared/services/ui/reactive-dialog.service';
import { Benutzer as BenutzerGsm, Kunde } from '@gsm/entities';
import { ObjektTyp } from '@o30/entities';
import { EVENT_TYPE_EDIT_KUNDE, EVENT_TYPE_IBAN_CHANGED, EVENT_TYPE_OPEN_FILE, EVENT_TYPE_UPLOAD_FILE, UploadFileEventModel } from 'app/shared/gsm/gsm-ui-lib-elements/src/app/dynamic-form-components/shared-models';
import { Observable } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private dialogService = inject(DialogService);
  private gsmService = inject(GsmService);
  private gsmErrorService = inject(GsmErrorService);
  private llapi = inject(LowLevelApi);
  private reactiveDialogService = inject(ReactiveDialogService);
  private destroyRef = inject(DestroyRef);
  private peziService = inject(PeziService);

  benutzer: BenutzerGsm;

  constructor() {
    this.benutzer = this.llapi.getValue('gsm.benutzer');
  }

  onCustomEvent(event: CustomEvent, bucketKey: string) {
    const eventType = event?.type;
    if (eventType === EVENT_TYPE_EDIT_KUNDE) {
      this._openEditKundeDialog(event.detail.data as Kunde, event.detail.resolve);
    } else if (eventType === EVENT_TYPE_OPEN_FILE) {
      try {
        this.gsmService.saveFile(bucketKey, event.detail as CustomFileModel);
      } catch (err) {
        this.gsmErrorService.catchError(err);
      }
    } else if (eventType === EVENT_TYPE_PRINT_PDF_INFOCLIENT && bucketKey) {
      this.peziService.openPeziWithLogin(`/analyse?value=${bucketKey}`, this.destroyRef);
    } else if (eventType === EVENT_TYPE_UPLOAD_FILE) {
      try {
        this.gsmService.uploadFileFromEvent(bucketKey, event.detail as UploadFileEventModel);
      } catch (err) {
        this.gsmErrorService.catchError(err);
      }
    } else if (eventType === EVENT_TYPE_IBAN_CHANGED) {
      try {
        const externalEmpfaenger = event.detail.value;
        const bic$ = this.gsmService.getBic(externalEmpfaenger.iban);
        bic$.subscribe(bic => {
          externalEmpfaenger.bic = bic;
          event.detail.patchValue(externalEmpfaenger);
        });
      } catch (err) {
        this.gsmErrorService.catchError(err);
      }
    }

  }

  private _openEditKundeDialog(data: Kunde, resolve: (editedData: Kunde) => void) {
    if (data) {
      this.editKundeDialog(data.kundeId)
        .pipe(
          first()
        )
        .subscribe(kunde => {
          if (kunde) {
            const {
              vorname,
              nachname,
              emailList,
              mobileList,
              bankVerbindungList
            } = kunde;
            const eventKunde: Kunde = {
              ...data,
              vorname,
              nachname,
              emailList,
              mobileList,
              bankVerbindungList
            };
            resolve(eventKunde);
          }
        });
    }
  }

  editKundeDialog(kundeId: string): Observable<Kunde> {
    const kundeIdNumber = parseInt(kundeId.slice(0, kundeId.indexOf('#')), 10);
    return this.dialogService.displayPersonErfassenDialog({
        objektTyp:  ObjektTyp.KUNDE,
        objId:      kundeIdNumber,
        kfa:        false,
        skipPolice: true,
        mode:       Mode.PREVENTNAMEEDIT
      })
      .afterClosed()
      .pipe(
        filter(dialogResult => dialogResult && dialogResult.data && dialogResult.statusId === DialogStatus.SAVE_SUCCESS),
        switchMap(() => this.gsmService.getKundeSimple(this.benutzer, kundeIdNumber))
      );
  }

  public openVirusDialog(errorMsg: string) {
    this.reactiveDialogService.open(GsmVirusWarningDialogComponent, {
      maxWidth: '800px',
      data:     {
        title: 'Warnung'
      }
    }, errorMsg);
  }
}
